import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftFromArc, faRotateRight, faStop } from "@fortawesome/pro-solid-svg-icons";
import SettingsSelect from "../Forms/SettingsSelect";
import Select from "react-select";
import { openModal } from "../../store/modal/modalReducer";

export default function BookingBotFlow({ current_staff, current_booking, ...props }) {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [botFlows, setBotFlows] = useState([]);
    const [years, setYears] = useState([]);
    const [currentBotFlow, setCurrentBotFlow] = useState(current_booking?.bot_flow_id);
    const [mainFlow, setMainFlow] = useState(null);
    const [subFlow, setSubFlow] = useState("");
    const [subFlows, setSubFlows] = useState([]);
    const [error, setError] = useState("");
    const [schedules, setSchedules] = useState([]);
    const [schedulesLoading, setSchedulesLoading] = useState(false);
    const [showAllSchedules, setShowAllSchedules] = useState(false);

    useEffect(() => {
        setInitialLoading(true);
        axios({
            method: "get",
            url: `${process.env.REACT_APP_API_URL}get_bot_flows`,
        })
            .then((response) => {
                setBotFlows(response.data[0]);
                setYears(response.data[1]);
            })
            .catch((error) => {
                alert("Failed to get bot flows");
            })
            .finally(() => {
                setInitialLoading(false);
            });

        // Also fetch schedules for this booking
        fetchSchedules();
    }, []);

    async function fetchSchedules() {
        if (!current_booking?.id) return;
        setSchedulesLoading(true);
        try {
            const res = await axios({
                method: "get",
                url: `${process.env.REACT_APP_API_URL}booking_botflow_schedules/${current_booking.id}`,
            });
            setSchedules(Array.isArray(res.data) ? res.data : res.data?.data || []);
        } catch (e) {
            // Silently fail but clear list
            setSchedules([]);
        } finally {
            setSchedulesLoading(false);
        }
    }

    // Cancel a specific pending schedule
    async function cancelSchedule(scheduleId) {
        if (!scheduleId) return;
        const confirmed = window.confirm("Cancel this scheduled bot flow?");
        if (!confirmed) return;
        try {
            await axios({
                method: "put",
                url: `${process.env.REACT_APP_API_URL}booking_botflow_schedules/${scheduleId}/cancel`,
                data: {
                    // optionally pass a trigger if needed later
                },
            });
            await fetchSchedules();
        } catch (e) {
            alert("Failed to cancel schedule");
        }
    }

    async function getBookingFlowStatus() {
        setRefreshing(true);
        await axios({
            method: "get",
            url: `${process.env.REACT_APP_API_URL}get_booking_flow_status/${current_booking?.id}`,
        })
            .then((response) => {
                setCurrentBotFlow(response.data);
            })
            .catch((error) => {
                alert("Failed to refresh booking flow status");
            })
            .finally(() => {
                // refresh schedules too
                fetchSchedules();
                setRefreshing(false);
            });
    }

    // Find the matching parent or child flow based on currentBotFlow
    function findMatchingFlow() {
        let foundFlow = null;

        // First, search if currentBotFlow matches a parent flow
        foundFlow = botFlows.find((botFlow) => botFlow.id === currentBotFlow);

        // If not found, search within the child items of each parent flow
        if (!foundFlow) {
            botFlows.forEach((parentFlow) => {
                if (parentFlow.children) {
                    const childMatch = parentFlow.children.find((child) => child.id === currentBotFlow);
                    if (childMatch) {
                        foundFlow = parentFlow; // Set the parent flow if child match is found
                    }
                }
            });
        }

        return foundFlow;
    }

    const matchedFlow = findMatchingFlow();

    async function stopBotFlow() {
        setLoading(true);
        await axios({
            method: "put",
            url: `${process.env.REACT_APP_API_URL}stop_bot_flow/${current_booking.id}`,
            data: {
                current_staff_id: current_staff.id,
            },
        })
            .then((response) => {
                getBookingFlowStatus();
            })
            .catch((error) => {
                alert("error");
            })
            .finally(() => {
                // dispatch(getBooking());
                setLoading(false);
                fetchSchedules();
            });
    }

    async function restartBotFlow() {
        setLoading(true);
        await axios({
            method: "put",
            url: `${process.env.REACT_APP_API_URL}restart_bot_flow/${current_booking.id}`,
            data: {
                current_staff_id: current_staff.id,
            },
        })
            .then((response) => {
                getBookingFlowStatus();
            })
            .catch((error) => {
                alert("error");
            })
            .finally(() => {
                setLoading(false);
                fetchSchedules();
            });
    }

    async function jumpToFlow(flowId, flowName) {
        const isCurrentFlow = flowId === currentBotFlow;
        const action = isCurrentFlow ? "restart" : "switch to";

        const confirmed = window.confirm(`Are you sure you want to ${action} "${flowName}"?`);
        if (!confirmed) return;

        if (isCurrentFlow) {
            // If clicking current flow, restart it
            restartBotFlow();
            return;
        }

        setLoading(true);
        await axios({
            method: "post",
            url: `${process.env.REACT_APP_API_URL}start_bot_flow`,
            data: {
                current_staff_id: current_staff.id,
                booking_id: current_booking?.id,
                mainFlow: flowId,
                subFlow: "",
            },
        })
            .then((response) => {
                getBookingFlowStatus();
            })
            .catch((error) => {
                alert("error");
            })
            .finally(() => {
                setLoading(false);
                fetchSchedules();
            });
    }

    // Handle the main flow selection
    const handleMainFlowChange = (e) => {
        const selectedFlowId = e.value;
        setMainFlow(selectedFlowId);

        // Find the selected main flow and get its children (sub-flows)
        const selectedFlow = botFlows.find((flow) => flow.id === parseInt(selectedFlowId));

        if (selectedFlow && selectedFlow.children) {
            setSubFlows(selectedFlow.children); // Update sub flows based on main flow
        } else {
            setSubFlows([]); // Reset if no sub flows
        }
    };

    // Handle the sub flow selection
    const handleSubFlowChange = (e) => {
        setSubFlow(e.target.value);
    };

    // Submit function with validation check
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Check if main flow is selected
        if (!mainFlow) {
            setError("Please select a main flow before submitting.");
            return;
        }

        setLoading(true);
        await axios({
            method: "post",
            url: `${process.env.REACT_APP_API_URL}start_bot_flow`,
            data: {
                current_staff_id: current_staff.id,
                booking_id: current_booking?.id,
                mainFlow: mainFlow,
                subFlow: subFlow,
            },
        })
            .then((response) => {
                getBookingFlowStatus();
            })
            .catch((error) => {
                alert("error");
            })
            .finally(() => {
                // dispatch(getBooking());
                setLoading(false);
                fetchSchedules();
            });
    };

    if (initialLoading === true) {
        return (
            <div className="flex items-center justify-center py-8">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    <p className="mt-2 text-gray-600">Loading bot flows...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="relative">
            {/* Loading Overlay */}
            {(loading || refreshing) && (
                <div className="absolute inset-0 bg-white bg-opacity-75 z-50 flex items-center justify-center">
                    <div className="text-center">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                        <p className="mt-3 text-gray-700 font-medium">
                            {refreshing ? "Refreshing..." : "Loading..."}
                        </p>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-2 gap-4 mb-4">
                <button 
                    className="bg-primary text-white px-4 py-2 rounded-full disabled:opacity-50" 
                    onClick={() => getBookingFlowStatus()}
                    disabled={loading || refreshing}
                >
                    Refresh
                </button>

                {currentBotFlow > 0 && (
                    <button 
                        className="bg-primary text-white px-4 py-2 rounded-full disabled:opacity-50" 
                        onClick={() => stopBotFlow()}
                        disabled={loading || refreshing}
                    >
                        Stop
                    </button>
                )}
            </div>

            {matchedFlow ? (
                <div>
                    <ul>
                        {/* Show the matched parent flow */}
                        <li className="flex justify-between items-center group" key={matchedFlow.id}>
                            <button
                                onClick={() => jumpToFlow(matchedFlow.id, matchedFlow.name)}
                                className="flex-1 text-left hover:text-primary transition-colors cursor-pointer py-1"
                                disabled={loading}
                            >
                                {matchedFlow.name}
                            </button>
                            {/* Show status next to the parent if it matches */}
                            {matchedFlow.id === currentBotFlow && <FontAwesomeIcon className="h-6 w-6 text-primary" aria-hidden="true" icon={faArrowLeftFromArc} />}
                        </li>

                        {/* If the parent has children, loop through and display them */}
                        {matchedFlow.children &&
                            matchedFlow.children.map((childFlow) => (
                                <li className="flex justify-between items-center ml-8 group" key={childFlow.id}>
                                    <button
                                        onClick={() => jumpToFlow(childFlow.id, childFlow.name)}
                                        className="flex-1 text-left hover:text-primary transition-colors cursor-pointer py-1"
                                        disabled={loading}
                                    >
                                        {childFlow.name}
                                    </button>
                                    {/* Show status next to the child if it matches */}
                                    {childFlow.id === currentBotFlow && <FontAwesomeIcon className="h-6 w-6 text-primary" aria-hidden="true" icon={faArrowLeftFromArc} />}
                                </li>
                            ))}
                    </ul>
                </div>
            ) : (
                <div>
                    <form onSubmit={handleSubmit}>
                        {error && <p className="text-red-500 mb-4">{error}</p>}

                        {/* Main Flow Dropdown */}
                        <div>
                            <label className="text-sm font-medium mb-2 text-gray-700 flex items-baseline justify-between" htmlFor="mainFlow">
                                Main Flow:
                            </label>
                            <Select
                                id="mainFlow"
                                options={(() => {
                                    // Separate main flows from regular flows
                                    const mainFlows = botFlows.filter((flow) => flow.is_main_flow === 1);
                                    const regularFlows = botFlows.filter((flow) => flow.is_main_flow !== 1);

                                    // Create grouped options
                                    const options = [];

                                    if (mainFlows.length > 0) {
                                        options.push({
                                            label: "Main Flows",
                                            options: mainFlows.map((flow) => ({ value: flow.id, label: flow.name })),
                                        });
                                    }

                                    if (regularFlows.length > 0) {
                                        options.push({
                                            label: "Other Flows",
                                            options: regularFlows.map((flow) => ({ value: flow.id, label: flow.name })),
                                        });
                                    }

                                    return options;
                                })()}
                                onChange={handleMainFlowChange}
                                value={
                                    mainFlow
                                        ? {
                                              value: mainFlow,
                                              label: botFlows.find((flow) => flow.id === mainFlow)?.name,
                                          }
                                        : null
                                }
                                components={{
                                    IndicatorSeparator: () => null,
                                }}
                                className="mb-4 mt-1 block font-light border-gray-300 rounded-lg shadow-sm focus:outline-primary sm:text-sm transition ease-linear duration-10 hover:border-primary"
                            />
                        </div>

                        {/* Sub Flow Dropdown */}
                        {subFlows.length > 0 && (
                            <div>
                                <label className="text-sm font-medium mb-2 text-gray-700 flex items-baseline justify-between" htmlFor="subFlow">
                                    Sub Flow:
                                </label>
                                <select
                                    className="mt-1 block border font-light border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-primary sm:text-sm transition ease-linear duration-10 hover:border-primary"
                                    id="subFlow"
                                    value={subFlow}
                                    onChange={handleSubFlowChange}
                                >
                                    <option value="">Select Sub Flow</option>
                                    {subFlows.map((subFlow) => (
                                        <option key={subFlow.id} value={subFlow.id}>
                                            {subFlow.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Submit Button */}
                        <div>
                            <button type="submit" className="bg-primary text-white px-4 py-2 rounded-full mt-4">
                                Start Flow
                            </button>
                        </div>

                        {(current_staff.id === 1 || current_staff.email === "resileads@qholdings.co.za") && (
                            <div className="mt-6">
                                <h3 className="text-sm font-medium text-gray-700 mb-3">Quick Start Flows:</h3>
                                <div className="grid grid-cols-2 gap-3">
                                    {botFlows
                                        ?.filter((flow) => {
                                            // Check for truly empty values, null, undefined, or whitespace-only strings
                                            return flow.button_name && flow.button_name !== "null" && flow.button_name.trim() !== "";
                                        })
                                        .map((flow) => (
                                            <button
                                                key={flow.id}
                                                onClick={async () => {
                                                    setLoading(true);
                                                    try {
                                                        await axios({
                                                            method: "post",
                                                            url: `${process.env.REACT_APP_API_URL}start_bot_flow`,
                                                            data: {
                                                                current_staff_id: current_staff.id,
                                                                booking_id: current_booking?.id,
                                                                mainFlow: flow.id,
                                                                subFlow: "",
                                                            },
                                                        });
                                                        getBookingFlowStatus();
                                                    } catch (error) {
                                                        alert("error");
                                                    } finally {
                                                        setLoading(false);
                                                    }
                                                }}
                                                className="bg-primary text-white px-4 py-2 rounded-full"
                                            >
                                                {flow.button_name}
                                            </button>
                                        ))}
                                </div>
                            </div>
                        )}
                    </form>
                </div>
            )}

            {/* Schedules Grid */}
            <div className="mt-8">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-gray-700">Bot Flow Schedules</h3>
                    <div className="flex items-center gap-2">
                        <button className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-800 px-3 py-1 rounded-full" onClick={() => setShowAllSchedules((v) => !v)}>
                            {showAllSchedules ? "Hide Completed" : "Show Completed"}
                        </button>
                    </div>
                </div>

                <div className="grid [grid-template-columns:repeat(auto-fill,minmax(240px,1fr))] gap-4">
                    {(schedulesLoading ? Array.from({ length: 4 }) : schedules.filter((s) => showAllSchedules || s.status === "pending")).map((s, idx) => {
                        const isLoading = schedulesLoading && !s?.id;
                        const name =
                            s?.bot_flow?.name ||
                            s?.bot_flow_name ||
                            (() => {
                                const match = botFlows.find((b) => b.id === s?.bot_flow_id);
                                return match ? match.name : s?.bot_flow_id ? `#${s.bot_flow_id}` : "—";
                            })();
                        const dateStr = s?.action_datestamp ? new Date(s.action_datestamp).toLocaleString() : schedulesLoading ? "Loading…" : "—";
                        const cancelledDateStr = s?.cancelled_at ? new Date(s.cancelled_at).toLocaleString() : null;
                        const rawStatus = s?.status || (schedulesLoading ? "Loading…" : "—");
                        const normalized = (s?.status || "").toLowerCase();
                        const statusClass = normalized === "completed" ? "bg-green-100 text-green-800" : normalized === "cancelled" ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800";
                        const prettyStatus = typeof rawStatus === "string" ? rawStatus.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : rawStatus;

                        return (
                            <div key={s?.id || idx} className={`relative border rounded-lg p-5 bg-white shadow-sm ${isLoading ? "animate-pulse" : ""}`}>
                                <div className="flex flex-col">
                                    <div className="text-sm font-medium text-gray-900 line-clamp-2">{name}</div>
                                    <div className="mt-2 text-xs text-gray-600">
                                        <div>Scheduled: {dateStr}</div>
                                        {cancelledDateStr && <div className="text-red-600">Cancelled: {cancelledDateStr}</div>}
                                    </div>
                                    <div className="mt-2">
                                        <span className={`px-2 inline-flex text-[11px] leading-5 font-semibold rounded-full ${statusClass}`}>{prettyStatus}</span>
                                    </div>
                                    {s?.cancellation_trigger && <div className="mt-1 text-[11px] text-gray-500">Trigger: {s.cancellation_trigger}</div>}
                                    <div className="mt-auto pt-3">
                                        {s?.status === "pending" && s?.id ? (
                                            <button
                                                className="w-full text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-full disabled:opacity-50"
                                                onClick={() => cancelSchedule(s.id)}
                                                disabled={schedulesLoading}
                                            >
                                                Cancel
                                            </button>
                                        ) : (
                                            <div className="text-[11px] text-gray-400 text-center">No actions</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {!schedulesLoading && schedules.filter((s) => showAllSchedules || s.status === "pending").length === 0 && (
                    <div className="px-4 py-6 text-center text-sm text-gray-500 border rounded-lg mt-3">No schedules found</div>
                )}
            </div>
        </div>
    );
}
